import { apiFetch } from "./client";
import type { AppCurrency } from "../lib/localePrefs";
import type { CartMap } from "../types";

export class CheckoutUnavailableError extends Error {}

export interface StripeCreateSessionPayload {
  status?: string;
  clientSecret?: string;
  publishableKey?: string;
  sessionId?: string;
  error?: string;
  message?: string;
  cart?: unknown;
}

declare global {
  interface Window {
    Stripe?: unknown;
  }
}

let stripeScriptPromise: Promise<void> | null = null;

/** Ensures Stripe.js (<https://js.stripe.com/v3/>) has loaded — matches legacy payment.html polling. */
export function ensureStripeJs(maxWaitMs = 15_000): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  const g = window as Window & { Stripe?: unknown };

  const waitForGlobal = (): Promise<void> =>
    new Promise((resolve, reject) => {
      const start = performance.now();
      const id = window.setInterval(() => {
        if (typeof g.Stripe !== "undefined") {
          window.clearInterval(id);
          resolve();
        } else if (performance.now() - start > maxWaitMs) {
          window.clearInterval(id);
          reject(new Error("Stripe.js konnte nicht geladen werden (Timeout)."));
        }
      }, 50);
    });

  if (typeof g.Stripe !== "undefined") return Promise.resolve();

  if (!stripeScriptPromise) {
    stripeScriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://js.stripe.com/v3/";
      const timeout = window.setTimeout(() => {
        script.remove();
        reject(new Error("Stripe.js konnte nicht geladen werden (Timeout)."));
      }, maxWaitMs);
      script.onload = () => { window.clearTimeout(timeout); resolve(); };
      script.onerror = () => {
        window.clearTimeout(timeout);
        script.remove();
        reject(new Error("Stripe.js Skript konnte nicht geladen werden."));
      };
      document.head.appendChild(script);
    }).catch(() => {
      stripeScriptPromise = null;
      throw new Error("Stripe.js konnte nicht geladen werden.");
    });
  }

  return stripeScriptPromise.then(() => waitForGlobal());
}

async function parseJsonBody<T>(res: Response): Promise<T> {
  const text = await res.text().catch(() => "");
  try {
    return JSON.parse(text) as T;
  } catch {
    return {} as T;
  }
}

export async function createStripeCheckoutSession(
  cart: CartMap,
  currency: AppCurrency,
  donation?: number | null,
): Promise<StripeCreateSessionPayload> {
  const payload: Record<string, unknown> = { cart, currency };
  if (donation !== undefined && donation !== null && donation > 0) {
    payload.donation = donation;
  }

  const res = await apiFetch("/stripe/create-checkout-session", {
    method: "POST",
    signal: AbortSignal.timeout(25_000),
    body: JSON.stringify(payload),
  });
  const data = await parseJsonBody<StripeCreateSessionPayload>(res);
  if (!res.ok) {
    const msg =
      typeof data.message === "string"
        ? data.message
        : typeof data.error === "string"
          ? data.error
          : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

export async function getStripeCheckoutSessionForResume(sessionId: string) {
  const res = await apiFetch(
    `/stripe/get-checkout-session?sessionId=${encodeURIComponent(sessionId)}`,
    { method: "GET", signal: AbortSignal.timeout(25_000), headers: { Accept: "application/json" } },
  );
  const data = await parseJsonBody<
    StripeCreateSessionPayload & { status?: string }
  >(res);
  if (res.status === 410 || (res.ok && data.status !== "pending")) {
    throw new CheckoutUnavailableError("Die vorherige Zahlung ist nicht mehr aktiv. Bitte eine neue Zahlung starten.");
  }
  if (!res.ok) {
    const msg =
      typeof data.error === "string"
        ? data.error
        : `Session konnte nicht wiederhergestellt werden (HTTP ${res.status}).`;
    throw new Error(msg);
  }
  return data;
}

/** Abort an open Stripe session (matches legacy DELETE on checkout id). */
export async function abortOrderCheckout(stripeSessionId: string) {
  return apiFetch(
    `/checkout/${encodeURIComponent(stripeSessionId)}`,
    { method: "DELETE" },
  );
}
